import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, Eye, EyeOff, Upload, X, Sparkles, Search,
  Tag, User, Clock, Globe, AlertCircle, CheckCircle2, ImageIcon,
} from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  BackButton, StatusBadge,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../ui';
import CommonToaster from '../Common/CommonToaster';
import AiGenerateModal from './AiGenerateModal';
import BlogImageDialog from './BlogImageDialog';
import { registerCodeBlocks, toggleCodeBlock } from './blogCode';
import {
  BLOG_IMAGE_TYPES, BODY_FORMATS, countImages, describeImageAt, imageAt,
  insertImageAt, replaceImageAt, uploadBlogImage, uploadPastedImages,
} from './blogImages';
import {
  getAdminBlog, createBlog, updateBlog, uploadCmsImage, aiGenerateBlog,
} from '../../utils/cmsAdminApi';
import '../SelfPaced/_self-paced-pages.scss';
import './cms-admin.scss';

registerCodeBlocks(Quill);

const quillToolbar = [
  [{ header: [2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike', 'code'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['blockquote', 'code-block', 'link', 'image'],
  ['clean'],
];

const emptyBlog = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  coverUrl: '',
  coverKey: '',
  tags: '',
  author: '',
  status: 'draft',
  seo: { metaTitle: '', metaDescription: '', metaKeywords: '' },
};

const SEO_LIMITS = { metaTitle: 60, metaDescription: 160 };

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return tmp.textContent || tmp.innerText || '';
}

function getWordCount(html) {
  const text = stripHtml(html).trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

function getReadTime(words) {
  return Math.max(1, Math.ceil(words / 200));
}

function getSeoScore(blog) {
  let score = 0;
  const issues = [];
  const passes = [];

  if (blog.title.trim()) { score += 15; passes.push('Title present'); }
  else issues.push('Add a blog title');

  if (blog.slug.trim()) { score += 10; passes.push('URL slug set'); }
  else issues.push('Set a URL slug');

  if (blog.excerpt.trim()) {
    score += 10;
    if (blog.excerpt.length >= 50 && blog.excerpt.length <= 300) {
      passes.push('Excerpt is a good length');
    } else {
      issues.push('Excerpt: aim for 50–300 characters');
    }
  } else issues.push('Add an excerpt for listings & social sharing');

  const words = getWordCount(blog.body);
  if (words >= 300) { score += 20; passes.push(`Content length good (${words} words)`); }
  else if (words > 0) { score += 8; issues.push(`Content too short — aim for 300+ words (${words} now)`); }
  else issues.push('Write the blog body content');

  if (blog.seo?.metaTitle?.trim()) {
    const len = blog.seo.metaTitle.length;
    if (len <= 60) { score += 15; passes.push('Meta title within 60 chars'); }
    else { score += 7; issues.push('Meta title is too long (keep ≤60 chars)'); }
  } else issues.push('Add an SEO meta title');

  if (blog.seo?.metaDescription?.trim()) {
    const len = blog.seo.metaDescription.length;
    if (len <= 160) { score += 15; passes.push('Meta description within 160 chars'); }
    else { score += 7; issues.push('Meta description too long (keep ≤160 chars)'); }
  } else issues.push('Add an SEO meta description');

  if (blog.coverUrl || blog.coverKey) { score += 10; passes.push('Cover image set'); }
  else issues.push('Add a cover image (boosts click-through rate)');

  const tags = blog.tags ? blog.tags.split(',').filter(t => t.trim()) : [];
  if (tags.length >= 2) { score += 5; passes.push(`${tags.length} tags added`); }
  else if (tags.length === 1) { score += 2; issues.push('Add at least 2 tags'); }
  else issues.push('Add tags for categorization');

  // Not scored: a post with no images is not a worse post.
  const images = countImages(blog.body);
  if (images.missingAlt > 0) {
    issues.push(images.missingAlt === 1
      ? 'Describe the image with no description (click it in the post)'
      : `Describe the ${images.missingAlt} images with no description (click one in the post)`);
  } else if (images.total > 0) {
    passes.push(images.total === 1 ? 'The image in the post is described' : `All ${images.total} images in the post are described`);
  }

  return { score: Math.min(100, score), issues, passes };
}

const BlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [blog, setBlog] = useState(emptyBlog);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiAudience, setAiAudience] = useState('students and parents evaluating courses');
  const [sideTab, setSideTab] = useState('seo');
  const quillRef = useRef(null);
  // { index, image } — image null to add one, { src, alt } to edit one.
  const [imageDialog, setImageDialog] = useState(null);
  const [imageUploads, setImageUploads] = useState(0);
  // Quill takes its modules once, at mount; their handlers read this ref so
  // they always see the current component.
  const imageActions = useRef({});

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });

  const getQuill = () => quillRef.current?.getEditor?.() || null;

  const quillModules = useMemo(() => ({
    toolbar: {
      container: quillToolbar,
      handlers: {
        image: () => imageActions.current.openDialog(),
        'code-block': function codeBlock(on) { toggleCodeBlock(this.quill, on); },
      },
    },
    // The language picker on each code block (see ./blogCode).
    codeBlocks: true,
    // Paste and drop: uploaded, never embedded as base64 (see ./blogImages).
    uploader: {
      mimetypes: BLOG_IMAGE_TYPES,
      handler: (range, files) => imageActions.current.uploadFiles(range, files),
    },
  }), []);

  imageActions.current.openDialog = () => {
    const quill = getQuill();
    if (!quill) return;
    const range = quill.getSelection(true);
    // The toolbar button on a selected image edits it — the keyboard's way in.
    const selected = range.length === 1 ? imageAt(quill, range.index) : null;
    setImageDialog(selected ? { index: selected.index, image: selected } : { index: range.index, image: null });
  };

  imageActions.current.uploadFiles = async (range, files) => {
    const quill = getQuill();
    if (!quill) return;
    if (range.length) quill.deleteText(range.index, range.length, 'user');
    // The author keeps typing while a photo uploads; follow their edits so the
    // image still lands where it was dropped. Our own inserts are not followed.
    const spot = { index: range.index };
    const follow = (delta) => { spot.index = delta.transformPosition(spot.index); };
    quill.on('text-change', follow);
    setImageUploads((n) => n + files.length);
    try {
      for (const file of files) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const url = await uploadBlogImage(file);
          quill.off('text-change', follow);
          spot.index = insertImageAt(quill, spot.index, { url });
          quill.on('text-change', follow);
        } catch (err) {
          showToast(err.message, 'error');
        } finally {
          setImageUploads((n) => n - 1);
        }
      }
    } finally {
      quill.off('text-change', follow);
    }
  };

  // Clicking an image in the post opens it in the dialog. Listened for on our
  // own wrapper, and the editor looked up at click time: ReactQuill replaces
  // its Quill (and the editing root) when it regenerates or remounts, and a
  // listener on the old root would silently stop working.
  const bodyFieldRef = useRef(null);
  useEffect(() => {
    const field = bodyFieldRef.current;
    if (loading || !field) return undefined;
    const onClick = (e) => {
      if (e.target?.tagName !== 'IMG') return;
      const quill = quillRef.current?.getEditor?.();
      if (!quill || !quill.root.contains(e.target)) return;
      const blot = Quill.find(e.target);
      const image = blot ? imageAt(quill, quill.getIndex(blot)) : null;
      if (!image) return;
      quill.setSelection(image.index, 1, 'silent');
      setImageDialog({ index: image.index, image });
    };
    field.addEventListener('click', onClick);
    return () => field.removeEventListener('click', onClick);
  }, [loading]);

  const submitImage = ({ url, alt }) => {
    const quill = getQuill();
    const target = imageDialog;
    setImageDialog(null);
    if (!quill || !target) return;
    if (!target.image) {
      insertImageAt(quill, target.index, { url, alt });
    } else {
      if (url !== target.image.src) replaceImageAt(quill, target.index, { url, alt });
      else describeImageAt(quill, target.index, alt);
      quill.setSelection(target.index + 1, 0, 'user');
    }
  };

  const removeImage = () => {
    const quill = getQuill();
    const target = imageDialog;
    setImageDialog(null);
    if (!quill || !target?.image) return;
    quill.deleteText(target.index, 1, 'user');
    quill.setSelection(target.index, 0, 'user');
  };

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await getAdminBlog(id);
      const b = res.data || {};
      setBlog({
        ...emptyBlog,
        ...b,
        tags: Array.isArray(b.tags) ? b.tags.join(', ') : '',
        seo: {
          metaTitle: b.seo?.metaTitle || '',
          metaDescription: b.seo?.metaDescription || '',
          metaKeywords: Array.isArray(b.seo?.metaKeywords)
            ? b.seo.metaKeywords.join(', ')
            : (b.seo?.metaKeywords || ''),
        },
      });
    } catch {
      showToast('Failed to load blog', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const upd = (field) => (e) => setBlog(b => ({ ...b, [field]: e.target.value }));
  const updSeo = (field) => (e) =>
    setBlog(b => ({ ...b, seo: { ...b.seo, [field]: e.target.value } }));

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      return showToast('Please choose an image file', 'error');
    }
    setUploading(true);
    try {
      const res = await uploadCmsImage(file, 'blog-cover');
      setBlog(b => ({ ...b, coverKey: res.key, coverUrl: res.url }));
      showToast('Cover uploaded');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearCover = () => setBlog(b => ({ ...b, coverKey: '', coverUrl: '' }));

  const handleSave = async (publish) => {
    if (!blog.title.trim()) return showToast('Title is required', 'error');
    setSaving(true);
    try {
      // Images pasted in with their text arrive as base64; upload them first.
      let body = blog.body;
      try {
        const pasted = await uploadPastedImages(body);
        if (pasted.count) {
          body = pasted.html;
          setBlog(b => ({ ...b, body }));
        }
      } catch (err) {
        showToast(`${err.message} The post was not saved.`, 'error');
        return;
      }

      const payload = {
        title: blog.title.trim(),
        slug: blog.slug.trim(),
        excerpt: blog.excerpt,
        body,
        coverUrl: blog.coverUrl,
        coverKey: blog.coverKey,
        author: blog.author,
        tags: blog.tags
          ? blog.tags.split(',').map(t => t.trim()).filter(Boolean)
          : [],
        status: publish ? 'published' : (blog.status || 'draft'),
        seo: {
          metaTitle: blog.seo?.metaTitle || '',
          metaDescription: blog.seo?.metaDescription || '',
          metaKeywords: blog.seo?.metaKeywords
            ? blog.seo.metaKeywords.split(',').map(k => k.trim()).filter(Boolean)
            : [],
        },
      };
      if (isNew) {
        const res = await createBlog(payload);
        showToast('Blog created');
        navigate(`/cms/blogs/${res.data._id}`, { replace: true });
      } else {
        await updateBlog(id, payload);
        showToast('Blog saved');
        load();
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to save blog', 'error');
    } finally {
      setSaving(false);
    }
  };

  const wordCount = useMemo(() => getWordCount(blog.body), [blog.body]);
  const readTime = useMemo(() => getReadTime(wordCount), [wordCount]);
  const seoData = useMemo(() => getSeoScore(blog), [blog]);

  if (loading) {
    return <div className="cms-admin-loading">Loading blog…</div>;
  }

  return (
    <div className="cms-admin-page">

      {/* Top Bar */}
      <div className="sp-form-topbar">
        <div className="sp-form-topbar__left">
          <BackButton onClick={() => navigate('/cms?tab=blogs')} />
          <div className="sp-form-topbar__info">
            <h5>{isNew ? 'New Blog Post' : 'Edit Blog Post'}</h5>
            <span className="blog-topbar-meta">
              <StatusBadge status={blog.status === 'published' ? 'active' : 'pending'}>
                {blog.status === 'published' ? 'Published' : 'Draft'}
              </StatusBadge>
              <span className="blog-topbar-stats">
                <Clock size={12} />
                {readTime} min read
                <span className="blog-topbar-dot" />
                {wordCount} words
              </span>
            </span>
          </div>
        </div>
        <div className="sp-form-topbar__actions">
          <AppButton variant="secondary" onClick={() => setAiOpen(true)} disabled={saving}>
            <Sparkles size={16} /> AI Generate
          </AppButton>
          <AppButton variant="secondary" onClick={() => handleSave(false)} disabled={saving || imageUploads > 0}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save Draft'}
          </AppButton>
          <AppButton variant="default" onClick={() => handleSave(true)} disabled={saving || imageUploads > 0}>
            <Eye size={16} /> {blog.status === 'published' ? 'Update' : 'Publish'}
          </AppButton>
        </div>
      </div>

      {/* Main 2-column layout */}
      <div className="blog-editor-layout">

        {/* ── Left: writing area ── */}
        <div className="blog-editor-content">

          {/* Title */}
          <div className="blog-editor-title-wrap">
            <input
              type="text"
              className="blog-editor-title-input"
              value={blog.title}
              onChange={upd('title')}
              placeholder="Write your blog title..."
            />
          </div>

          {/* Slug + Author */}
          <div className="blog-editor-meta-row">
            <div className="cms-editor-field blog-editor-field--slug">
              <label htmlFor="slug"><Globe size={12} /> URL Slug</label>
              <input
                id="slug"
                type="text"
                value={blog.slug}
                onChange={upd('slug')}
                placeholder="auto-generated-from-title"
              />
            </div>
            <div className="cms-editor-field">
              <label htmlFor="author"><User size={12} /> Author</label>
              <input
                id="author"
                type="text"
                value={blog.author}
                onChange={upd('author')}
                placeholder="Author name"
              />
            </div>
          </div>

          {/* Excerpt */}
          <div className="cms-editor-field">
            <label htmlFor="excerpt">
              Excerpt
              <span className="blog-char-count">{blog.excerpt.length}/300</span>
            </label>
            <textarea
              id="excerpt"
              rows={2}
              value={blog.excerpt}
              onChange={upd('excerpt')}
              placeholder="A compelling summary shown in blog listings and social shares…"
              maxLength={300}
            />
          </div>

          {/* Body */}
          <div ref={bodyFieldRef} className="cms-editor-field cms-editor-field--quill">
            <label>
              Body Content
              <span className="blog-char-count" role="status">
                {imageUploads > 0 && (imageUploads === 1 ? 'Uploading an image…' : `Uploading ${imageUploads} images…`)}
              </span>
            </label>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={blog.body}
              onChange={(value) => setBlog(b => ({ ...b, body: value }))}
              modules={quillModules}
              formats={BODY_FORMATS}
              placeholder="Start writing your blog content…"
            />
            <small>
              Add an image where the cursor is with the image button, or paste or drop one in. Click an image to describe, change or remove it.
              For code, press the code block button, paste the code in and pick its language at the block's top right.
            </small>
          </div>

          {/* Tags */}
          <div className="cms-editor-field">
            <label htmlFor="tags"><Tag size={12} /> Tags (comma-separated)</label>
            <input
              id="tags"
              type="text"
              value={blog.tags}
              onChange={upd('tags')}
              placeholder="e.g., career, web development, java, placements"
            />
          </div>
        </div>

        {/* ── Right: sidebar ── */}
        <aside className="blog-editor-sidebar">
          <Tabs value={sideTab} onChange={setSideTab}>
            <TabsList>
              <TabsTrigger value="seo"><Search size={13} /> SEO</TabsTrigger>
              <TabsTrigger value="media"><ImageIcon size={13} /> Cover</TabsTrigger>
            </TabsList>

            {/* SEO tab */}
            <TabsContent value="seo">
              {/* Score card */}
              <AppCard className="blog-seo-score-card">
                <AppCardContent>
                  <div className="blog-seo-score">
                    <div className={`blog-seo-score__ring ${seoData.score >= 80 ? 'good' : seoData.score >= 50 ? 'ok' : 'poor'}`}>
                      <span>{seoData.score}</span>
                    </div>
                    <div className="blog-seo-score__info">
                      <strong>SEO Score</strong>
                      <small>
                        {seoData.score >= 80 ? 'Great — ready to publish.' :
                         seoData.score >= 50 ? 'Good, a bit more to improve.' :
                         'Needs work before publishing.'}
                      </small>
                    </div>
                  </div>
                </AppCardContent>
              </AppCard>

              {/* Checklist */}
              <AppCard>
                <AppCardContent>
                  <div className="blog-seo-list">
                    {seoData.issues.map((issue, i) => (
                      <div key={i} className="blog-seo-item blog-seo-item--issue">
                        <AlertCircle size={12} /> <span>{issue}</span>
                      </div>
                    ))}
                    {seoData.passes.length > 0 && (
                      <div className={`blog-seo-passes ${seoData.issues.length > 0 ? 'blog-seo-passes--separated' : ''}`}>
                        {seoData.passes.map((p, i) => (
                          <div key={i} className="blog-seo-item blog-seo-item--pass">
                            <CheckCircle2 size={12} /> <span>{p}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </AppCardContent>
              </AppCard>

              {/* Meta fields */}
              <AppCard>
                <AppCardHeader>
                  <AppCardTitle>Meta Tags</AppCardTitle>
                </AppCardHeader>
                <AppCardContent>
                  <div className="cms-editor-field">
                    <label htmlFor="metaTitle">
                      Meta Title
                      <span className={`blog-char-count ${(blog.seo?.metaTitle?.length || 0) > SEO_LIMITS.metaTitle ? 'blog-char-count--over' : ''}`}>
                        {blog.seo?.metaTitle?.length || 0}/{SEO_LIMITS.metaTitle}
                      </span>
                    </label>
                    <input
                      id="metaTitle"
                      type="text"
                      value={blog.seo?.metaTitle || ''}
                      onChange={updSeo('metaTitle')}
                      placeholder="SEO title shown in search results"
                    />
                  </div>
                  <div className="cms-editor-field">
                    <label htmlFor="metaDescription">
                      Meta Description
                      <span className={`blog-char-count ${(blog.seo?.metaDescription?.length || 0) > SEO_LIMITS.metaDescription ? 'blog-char-count--over' : ''}`}>
                        {blog.seo?.metaDescription?.length || 0}/{SEO_LIMITS.metaDescription}
                      </span>
                    </label>
                    <textarea
                      id="metaDescription"
                      rows={3}
                      value={blog.seo?.metaDescription || ''}
                      onChange={updSeo('metaDescription')}
                      placeholder="Concise description shown under the title in search results"
                    />
                  </div>
                  <div className="cms-editor-field">
                    <label htmlFor="metaKeywords">Focus Keywords</label>
                    <input
                      id="metaKeywords"
                      type="text"
                      value={blog.seo?.metaKeywords || ''}
                      onChange={updSeo('metaKeywords')}
                      placeholder="primary keyword, secondary keyword"
                    />
                  </div>
                </AppCardContent>
              </AppCard>

              {/* SERP Preview */}
              <AppCard>
                <AppCardHeader>
                  <AppCardTitle>Search Preview</AppCardTitle>
                </AppCardHeader>
                <AppCardContent>
                  <div className="blog-serp-preview">
                    <div className="blog-serp-preview__title">
                      {blog.seo?.metaTitle || blog.title || 'Blog Post Title'}
                    </div>
                    <div className="blog-serp-preview__url">
                      yoursite.com/blogs/{blog.slug || 'blog-post-slug'}
                    </div>
                    <div className="blog-serp-preview__desc">
                      {blog.seo?.metaDescription || blog.excerpt || 'Blog description will appear here…'}
                    </div>
                  </div>
                </AppCardContent>
              </AppCard>
            </TabsContent>

            {/* Media tab */}
            <TabsContent value="media">
              <AppCard>
                <AppCardHeader>
                  <AppCardTitle>Cover Image</AppCardTitle>
                </AppCardHeader>
                <AppCardContent>
                  <p className="blog-cover-hint">Recommended: 1200×630 px for optimal social sharing</p>

                  {blog.coverUrl ? (
                    <div className="blog-cover-preview">
                      <img src={blog.coverUrl} alt="Cover preview" />
                      <button className="blog-cover-preview__remove" onClick={clearCover} title="Remove">
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="blog-cover-dropzone"
                      onClick={() => fileInputRef.current?.click()}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                    >
                      <Upload size={22} />
                      <span>Click to upload cover image</span>
                      <small>JPG, PNG, WebP</small>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleCoverUpload}
                    className="cms-hidden-input"
                  />

                  {blog.coverUrl && (
                    <AppButton
                      variant="secondary"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <Upload size={14} /> {uploading ? 'Uploading…' : 'Change Image'}
                    </AppButton>
                  )}

                  <div className="cms-editor-field cms-editor-field--gap-top">
                    <label htmlFor="coverUrl">Or paste image URL</label>
                    <input
                      id="coverUrl"
                      type="url"
                      value={blog.coverKey ? '' : blog.coverUrl}
                      onChange={(e) => setBlog(b => ({ ...b, coverUrl: e.target.value, coverKey: '' }))}
                      placeholder="https://…"
                      disabled={!!blog.coverKey}
                    />
                  </div>
                </AppCardContent>
              </AppCard>
            </TabsContent>
          </Tabs>
        </aside>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />

      <BlogImageDialog
        show={Boolean(imageDialog)}
        image={imageDialog?.image || null}
        onSubmit={submitImage}
        onRemove={removeImage}
        onHide={() => setImageDialog(null)}
      />

      <AiGenerateModal
        show={aiOpen}
        onHide={() => setAiOpen(false)}
        title="Generate Blog Post with AI"
        description="AI will draft a complete SEO-optimized blog post — title, excerpt, body, tags, and meta fields. Review and edit before publishing."
        showLength
        extraFields={[
          {
            key: 'topic', label: 'Blog topic', type: 'text', required: true,
            placeholder: 'e.g. How students should choose between MERN and Java fullstack tracks',
            value: aiTopic, onChange: setAiTopic,
          },
          {
            key: 'audience', label: 'Target audience', type: 'text',
            placeholder: 'e.g. parents of 12th-pass students looking for placement-focused courses',
            value: aiAudience, onChange: setAiAudience,
          },
        ]}
        onGenerate={async ({ prompt, tone, length, ...ai }, { regenerate }) => {
          if (!aiTopic.trim()) {
            throw new Error('Please enter a blog topic.');
          }
          return aiGenerateBlog({
            topic: aiTopic.trim(),
            audience: aiAudience,
            length,
            tone,
            prompt,
            regenerate,
            // The admin's model pick. A 1,000-word post is exactly the job the
            // institute's default chat model is worst at, so this matters here.
            ...ai,
          });
        }}
        onApply={(data) => {
          setBlog((b) => ({
            ...b,
            title: data.title || b.title,
            slug: data.slug || b.slug,
            excerpt: data.excerpt || b.excerpt,
            body: data.body || b.body,
            tags: Array.isArray(data.tags) ? data.tags.join(', ') : (b.tags || ''),
            seo: {
              metaTitle: data.seo?.metaTitle || b.seo?.metaTitle || '',
              metaDescription: data.seo?.metaDescription || b.seo?.metaDescription || '',
              metaKeywords: Array.isArray(data.seo?.metaKeywords)
                ? data.seo.metaKeywords.join(', ')
                : (b.seo?.metaKeywords || ''),
            },
          }));
          showToast('AI draft applied — review the SEO score then publish.');
        }}
        renderPreview={(d) => (
          <div className="cms-ai-preview">
            <p><strong>Title:</strong> {d.title}</p>
            {d.excerpt && <p><strong>Excerpt:</strong> {d.excerpt}</p>}
            {Array.isArray(d.tags) && d.tags.length > 0 && (
              <p><strong>Tags:</strong> {d.tags.join(', ')}</p>
            )}
            {d.seo?.metaTitle && <p><strong>Meta Title:</strong> {d.seo.metaTitle}</p>}
            {d.seo?.metaDescription && <p><strong>Meta Desc:</strong> {d.seo.metaDescription}</p>}
            {d.body && (
              <details open>
                <summary><strong>Body preview</strong></summary>
                <div
                  className="cms-ai-preview__body"
                  dangerouslySetInnerHTML={{ __html: d.body }}
                />
              </details>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default BlogEditor;
